export const exportComprehensiveSaleReportToPDF = (data) => {
  const {
    sales,
    payments,
    returns,
    balancePayments = [],
    summary,
    selectedSaleType = "all",
    selectedPaymentStatus = "all",
  } = data;

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object" && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(numStr) || 0;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSaleType = (sale) => {
    const items = Array.isArray(sale?.sale_items) ? sale.sale_items : [];
    if (!items.length) return "retail";

    const types = new Set(
      items.map((item) => {
        const salePrice = parseAmount(item?.sale_price);
        const wholesalePrice = parseAmount(item?.product_details?.wholesale_price);
        const retailPrice = parseAmount(
          item?.product_details?.retail_price ?? item?.product_details?.sale_price
        );

        if (wholesalePrice > 0 && salePrice === wholesalePrice) return "wholesale";
        if (retailPrice > 0 && salePrice === retailPrice) return "retail";
        if (wholesalePrice > 0 && retailPrice <= 0) return "wholesale";
        return "retail";
      })
    );

    return types.size === 1 ? Array.from(types)[0] : "mixed";
  };

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>📊 Comprehensive Sales Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.6;
            color: #102a43;
            background: #eef4fb;
            min-height: 100vh;
            padding: 20px;
          }
          
          .container {
            background: white;
            border-radius: 15px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 30px 20px;
            position: relative;
          }
          
          .header h1 { 
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          
          .content {
            padding: 30px;
          }
          
          .summary-grid { 
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .summary-card { 
            background: #ffffff;
            color: #102a43;
            padding: 25px;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
            position: relative;
            overflow: hidden;
            border: 1px solid #d9e2ec;
          }
          
          .summary-card.sales { background: #eef6ff; }
          .summary-card.payments { background: #edfdf3; }
          .summary-card.returns { background: #fff4f4; }
          .summary-card.outstanding { background: #fff8e6; color: #102a43; }
          .summary-card.net { background: #f4ecff; }
          
          .summary-card h3 { 
            font-size: 14px;
            margin-bottom: 10px;
            opacity: 0.9;
          }
          
          .summary-card .amount { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          
          .summary-card .count { 
            font-size: 12px;
            opacity: 0.8;
          }
          
          .section { 
            margin: 40px 0;
            page-break-inside: avoid;
          }
          
          .section-header {
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            padding: 15px 20px;
            border-radius: 8px 8px 0 0;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .table-container {
            background: white;
            border-radius: 0 0 8px 8px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          table { 
            width: 100%;
            border-collapse: collapse;
          }
          
          th { 
            background: #12344d;
            color: #ffffff;
            font-weight: 600;
            padding: 15px 12px;
            text-align: left;
            border-bottom: 2px solid #0f2f46;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td { 
            padding: 12px;
            border-bottom: 1px solid #f1f3f4;
            font-size: 11px;
            vertical-align: middle;
          }
          
          tr:nth-child(even) { 
            background: #fafbfc;
          }
          
          .summary-row { 
            background: linear-gradient(90deg, #667eea, #764ba2) !important;
            color: white !important;
            font-weight: 600;
          }
          
          .summary-row td {
            border-color: rgba(255,255,255,0.2);
            padding: 15px 12px;
          }
          
          .status-badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-paid { background: #d4edda; color: #155724; }
          .status-partial { background: #fff3cd; color: #856404; }
          .status-unpaid { background: #f8d7da; color: #721c24; }
          
          .amount-cell { 
            text-align: right;
            font-weight: 700;
            color: #102a43;
            font-family: 'Courier New', monospace;
          }
          
          .id-cell {
            font-weight: 600;
            color: #6c757d;
          }
          
          .footer { 
            margin-top: 50px;
            padding: 30px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 12px;
            text-align: center;
            color: #6c757d;
            border-top: 3px solid #667eea;
          }
          
          .footer-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .footer-stat {
            text-align: center;
          }
          
          .footer-stat .number {
            font-size: 18px;
            font-weight: 700;
            color: #495057;
            display: block;
          }
          
          .footer-stat .label {
            font-size: 11px;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          @media print {
            body { 
              background: white;
              padding: 0;
            }
            .container {
              box-shadow: none;
              border-radius: 0;
            }
            .section { 
              page-break-inside: avoid;
            }
            .summary-grid { 
              page-break-inside: avoid;
            }
          }
          
          @page {
            margin: 0.5in;
            size: A4;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 Comprehensive Sales Report</h1>
            <p>Generated on ${getCurrentDate()}</p>
            <p>Sale type filter: ${selectedSaleType}</p>
            <p>Payment status filter: ${selectedPaymentStatus}</p>
          </div>
          
          <div class="content">
            ${
              summary && Object.keys(summary).length > 0
                ? `
            <div class="summary-grid">
              ${
                summary.previous_balance != null &&
                Number(summary.previous_balance) !== 0
                  ? `
              <div class="summary-card" style="background: linear-gradient(135deg, #434343 0%, #000000 100%); color: white;">
                <h3>📌 Previous Balance (Opening)</h3>
                <div class="amount">${formatCurrency(
                  summary.previous_balance || 0,
                )} ${summary.previous_balance_currency || ""}</div>
                <div class="count">Customer opening balance</div>
              </div>
              `
                  : ""
              }
              <div class="summary-card sales">
                <h3>🛒 Total Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.total_sales?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_sales?.count || 0
                } transactions</div>
                <div class="count">Discount ${formatCurrency(
                  summary.total_sales?.discount_amount || 0,
                )}</div>
              </div>
              <div class="summary-card payments">
                <h3>💳 Revenue (Collected)</h3>
                <div class="amount">${formatCurrency(
                  summary.total_revenue ?? summary.total_paid ?? 0,
                )}</div>
                <div class="count">Sale + Balance payments</div>
              </div>
              <div class="summary-card outstanding">
                <h3>⚠️ Outstanding</h3>
                <div class="amount">${formatCurrency(
                  summary.outstanding_including_previous != null
                    ? summary.outstanding_including_previous
                    : summary.outstanding_amount || 0,
                )}</div>
                <div class="count">${
                  summary.outstanding_including_previous != null
                    ? summary.is_all_customers
                      ? "Opening balance + Sales (after returns)"
                      : "Previous + Sales (after returns)"
                    : "After returns"
                }</div>
              </div>
              <div class="summary-card net">
                <h3>🏷️ Wholesale Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.sale_type_breakdown?.wholesale?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.sale_type_breakdown?.wholesale?.count || 0
                } sales</div>
              </div>
              <div class="summary-card net">
                <h3>🛍️ Retail Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.sale_type_breakdown?.retail?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.sale_type_breakdown?.retail?.count || 0
                } sales</div>
              </div>
              <div class="summary-card returns">
                <h3>↩️ Total Returns</h3>
                <div class="amount">${formatCurrency(
                  summary.total_returns?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_returns?.count || 0
                } returns</div>
              </div>
              <div class="summary-card net">
                <h3>💰 Net Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.net_sales || 0,
                )}</div>
                <div class="count">Final value</div>
              </div>
              ${
                (summary.total_balance_payments?.count ?? 0) > 0
                  ? `
              <div class="summary-card" style="background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); color: #2c3e50;">
                <h3>📋 Balance Payments</h3>
                <div class="amount">${formatCurrency(
                  summary.total_balance_payments?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_balance_payments?.count || 0
                } payments</div>
              </div>
              `
                  : ""
              }
            </div>
            `
                : ""
            }

            ${
              sales && sales.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                🛒 Sales Transactions (${sales.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 8%">ID</th>
                      <th style="width: 18%">Customer</th>
                      <th style="width: 12%">Date</th>
                      <th style="width: 10%">Type</th>
                      <th style="width: 12%">Total Amount</th>
                      <th style="width: 10%">Discount</th>
                      <th style="width: 11%">Amount Paid</th>
                      <th style="width: 9%">Returns</th>
                      <th style="width: 11%">Outstanding</th>
                      <th style="width: 7%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${sales
                      .map((sale) => {
                        const saleReturns = returns.filter(
                          (r) => r.sale === sale.id,
                        );
                        const returnAmount = saleReturns.reduce(
                          (sum, r) => sum + parseAmount(r.total_refound_amount),
                          0,
                        );
                        const subtotalAmount = parseAmount(sale.total_amount);
                        const discountAmount = parseAmount(sale.discount || 0);
                        const totalAmount = parseAmount(
                          sale.effective_total_amount ?? sale.total_amount,
                        );
                        const paidAmount = parseAmount(sale.total_paid || 0);
                        const outstanding = Math.max(
                          0,
                          totalAmount - paidAmount - returnAmount,
                        );
                        const status =
                          outstanding <= 0
                            ? "Paid"
                            : paidAmount > 0
                              ? "Partial"
                              : "Unpaid";
                        const statusClass =
                          outstanding <= 0
                            ? "status-paid"
                            : paidAmount > 0
                              ? "status-partial"
                              : "status-unpaid";

                        return `
                        <tr>
                          <td class="id-cell">#${sale.id}</td>
                          <td>${
                            sale.customer_details?.name || "Walk-in Customer"
                          }</td>
                          <td>${formatDate(sale.sale_date)}</td>
                          <td style="text-transform: capitalize;">${getSaleType(sale)}</td>
                          <td class="amount-cell">${formatCurrency(
                            subtotalAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            discountAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            paidAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            returnAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            outstanding,
                          )}</td>
                          <td><span class="status-badge ${statusClass}">${status}</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>📊 TOTAL SUMMARY</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_sales?.amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_sales?.discount_amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_paid || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_returns?.amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.outstanding_including_previous != null
                          ? summary.outstanding_including_previous
                          : summary.outstanding_amount || 0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              payments && payments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                💳 Payment Transactions (${payments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Payment ID</th>
                      <th style="width: 15%">Sale ID</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 25%">Payment Date</th>
                      <th style="width: 15%">Method</th>
                      <th style="width: 15%">Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${payments
                      .map(
                        (payment) => `
                      <tr>
                        <td class="id-cell">#${payment.id}</td>
                        <td>Sale #${payment.sale}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(payment.amount),
                        )}</td>
                        <td>${formatDate(payment.payment_date)}</td>
                        <td>${payment.payment_method || "Cash"}</td>
                        <td>${payment.currency_details?.title || "USD"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>💰 TOTAL SALE PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        payments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              balancePayments && balancePayments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                📋 Balance Payments (${balancePayments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">ID</th>
                      <th style="width: 25%">Customer</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 20%">Payment Date</th>
                      <th style="width: 15%">Currency</th>
                      <th style="width: 10%">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${balancePayments
                      .map(
                        (bp) => `
                      <tr>
                        <td class="id-cell">#${bp.id}</td>
                        <td>${bp.customer_details?.name || "-"}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(bp.amount),
                        )}</td>
                        <td>${formatDate(bp.payment_date)}</td>
                        <td>${bp.currency_details?.title || "-"}</td>
                        <td>${bp.notes || "-"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>📋 TOTAL BALANCE PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_balance_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        balancePayments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              returns && returns.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                ↩️ Return Transactions (${returns.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Return ID</th>
                      <th style="width: 15%">Sale ID</th>
                      <th style="width: 20%">Return Date</th>
                      <th style="width: 25%">Reason</th>
                      <th style="width: 15%">Refund Amount</th>
                      <th style="width: 10%">Items Count</th>
                      <th style="width: 5%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${returns
                      .map((returnItem) => {
                        const refundAmount = parseAmount(
                          returnItem.total_refound_amount || 0,
                        );
                        return `
                        <tr>
                          <td class="id-cell">#${returnItem.id}</td>
                          <td>Sale #${returnItem.sale}</td>
                          <td>${formatDate(returnItem.return_date)}</td>
                          <td>${returnItem.reason || "No reason provided"}</td>
                          <td class="amount-cell">${formatCurrency(
                            refundAmount,
                          )}</td>
                          <td style="text-align: center">${
                            returnItem.return_items?.length || 0
                          }</td>
                          <td><span class="status-badge status-paid">✓</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>🔄 TOTAL RETURNS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_returns?.amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${returns.reduce(
                        (sum, r) => sum + (r.return_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            <div class="footer">
              <div class="footer-stats">
                <div class="footer-stat">
                  <span class="number">${sales?.length || 0}</span>
                  <span class="label">Sales</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${payments?.length || 0}</span>
                  <span class="label">Sale Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${balancePayments?.length || 0}</span>
                  <span class="label">Balance Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.total_revenue ?? summary.total_paid ?? 0,
                  )}</span>
                  <span class="label">Revenue</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${returns?.length || 0}</span>
                  <span class="label">Returns</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.outstanding_including_previous != null
                      ? summary.outstanding_including_previous
                      : summary.outstanding_amount || 0,
                  )}</span>
                  <span class="label">Outstanding</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.net_sales || 0,
                  )}</span>
                  <span class="label">Net Sales</span>
                </div>
              </div>
              <p><strong>📊 Comprehensive Sales Report</strong> • Generated by Inventory Management System</p>
              <p>Report generated on ${getCurrentDate()}</p>
              <p style="margin-top:8px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</p>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Auto-trigger print dialog after content loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    };
  } else {
    alert("Please allow popups to export the report");
  }

  return printWindow;
};

export const exportComprehensivePurchaseReportToPDF = (data) => {
  const { purchases, payments, returns, summary, suppliers } = data;

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object" && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(numStr) || 0;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>📊 Comprehensive Purchase Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.6;
            color: #2c3e50;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 20px;
          }
          
          .container {
            background: white;
            border-radius: 15px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 30px 20px;
            position: relative;
          }
          
          .header h1 { 
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          
          .content {
            padding: 30px;
          }
          
          .summary-grid { 
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .summary-card { 
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 25px;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
            position: relative;
            overflow: hidden;
          }
          
          .summary-card.purchases { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
          .summary-card.payments { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
          .summary-card.returns { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
          .summary-card.outstanding { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); color: #2c3e50; }
          
          .summary-card h3 { 
            font-size: 14px;
            margin-bottom: 10px;
            opacity: 0.9;
          }
          
          .summary-card .amount { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          
          .summary-card .count { 
            font-size: 12px;
            opacity: 0.8;
          }
          
          .section { 
            margin: 40px 0;
            page-break-inside: avoid;
          }
          
          .section-header {
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            padding: 15px 20px;
            border-radius: 8px 8px 0 0;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .table-container {
            background: white;
            border-radius: 0 0 8px 8px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          table { 
            width: 100%;
            border-collapse: collapse;
          }
          
          th { 
            background: linear-gradient(90deg, #f8f9fa, #e9ecef);
            color: #495057;
            font-weight: 600;
            padding: 15px 12px;
            text-align: left;
            border-bottom: 2px solid #dee2e6;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td { 
            padding: 12px;
            border-bottom: 1px solid #f1f3f4;
            font-size: 11px;
            vertical-align: middle;
          }
          
          tr:nth-child(even) { 
            background: #fafbfc;
          }
          
          tr:hover { 
            background: #e3f2fd;
            transition: background-color 0.2s ease;
          }
          
          .summary-row { 
            background: linear-gradient(90deg, #667eea, #764ba2) !important;
            color: white !important;
            font-weight: 600;
          }
          
          .summary-row td {
            border-color: rgba(255,255,255,0.2);
            padding: 15px 12px;
          }
          
          .status-badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-paid { background: #d4edda; color: #155724; }
          .status-partial { background: #fff3cd; color: #856404; }
          .status-unpaid { background: #f8d7da; color: #721c24; }
          
          .amount-cell { 
            text-align: right;
            font-weight: 600;
            color: #28a745;
            font-family: 'Courier New', monospace;
          }
          
          .id-cell {
            font-weight: 600;
            color: #6c757d;
          }
          
          .footer { 
            margin-top: 50px;
            padding: 30px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 12px;
            text-align: center;
            color: #6c757d;
            border-top: 3px solid #667eea;
          }
          
          .footer-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .footer-stat {
            text-align: center;
          }
          
          .footer-stat .number {
            font-size: 18px;
            font-weight: 700;
            color: #495057;
            display: block;
          }
          
          .footer-stat .label {
            font-size: 11px;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          @media print {
            body { 
              background: white;
              padding: 0;
            }
            .container {
              box-shadow: none;
              border-radius: 0;
            }
            .section { 
              page-break-inside: avoid;
            }
            .summary-grid { 
              page-break-inside: avoid;
            }
          }
          
          @page {
            margin: 0.5in;
            size: A4;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 Comprehensive Purchase Report</h1>
            <p>Generated on ${getCurrentDate()}</p>
          </div>
          
          <div class="content">
            ${
              summary && Object.keys(summary).length > 0
                ? `
            <div class="summary-grid">
              <div class="summary-card purchases">
                <h3>🛒 Total Purchases</h3>
                <div class="amount">${formatCurrency(
                  summary.total_purchases?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_purchases?.count || 0
                } transactions</div>
              </div>
              <div class="summary-card payments">
                <h3>💳 Total Paid</h3>
                <div class="amount">${formatCurrency(
                  summary.total_paid || 0,
                )}</div>
                <div class="count">${
                  summary.total_payments?.count || 0
                } payments</div>
              </div>
              <div class="summary-card outstanding">
                <h3>⚠️ Outstanding</h3>
                <div class="amount">${formatCurrency(
                  summary.outstanding_amount || 0,
                )}</div>
                <div class="count">Remaining balance</div>
              </div>
              <div class="summary-card returns">
                <h3>↩️ Total Returns</h3>
                <div class="amount">${formatCurrency(
                  summary.total_returns?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_returns?.count || 0
                } returns</div>
              </div>
            </div>
            `
                : ""
            }

            ${
              purchases && purchases.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                🛒 Purchase Transactions (${purchases.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 8%">ID</th>
                      <th style="width: 20%">Supplier</th>
                      <th style="width: 12%">Date</th>
                      <th style="width: 15%">Total Amount</th>
                      <th style="width: 15%">Amount Paid</th>
                      <th style="width: 15%">Remaining</th>
                      <th style="width: 8%">Items</th>
                      <th style="width: 7%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${purchases
                      .map((purchase) => {
                        const totalPrice = parseAmount(purchase.total_price);
                        const totalPaid = parseAmount(purchase.total_paid);
                        const remaining = parseAmount(
                          purchase.total_remained_amount,
                        );
                        const status =
                          remaining === 0
                            ? "Paid"
                            : totalPaid > 0
                              ? "Partial"
                              : "Unpaid";
                        const statusClass =
                          remaining === 0
                            ? "status-paid"
                            : totalPaid > 0
                              ? "status-partial"
                              : "status-unpaid";

                        return `
                        <tr>
                          <td class="id-cell">#${purchase.id}</td>
                          <td>${purchase.supplier_details?.name || "N/A"}</td>
                          <td>${formatDate(purchase.purchase_date)}</td>
                          <td class="amount-cell">${formatCurrency(
                            totalPrice,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            totalPaid,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            remaining,
                          )}</td>
                          <td style="text-align: center">${
                            purchase.purchase_items?.length || 0
                          }</td>
                          <td><span class="status-badge ${statusClass}">${status}</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="3"><strong>📊 TOTAL SUMMARY</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_purchases?.amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_paid || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.outstanding_amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${purchases.reduce(
                        (sum, p) => sum + (p.purchase_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              payments && payments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                💳 Payment Transactions (${payments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Payment ID</th>
                      <th style="width: 15%">Purchase ID</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 25%">Payment Date</th>
                      <th style="width: 15%">Currency</th>
                      <th style="width: 15%">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${payments
                      .map(
                        (payment) => `
                      <tr>
                        <td class="id-cell">#${payment.id}</td>
                        <td>Purchase #${payment.purchase}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(payment.amount),
                        )}</td>
                        <td>${formatDate(payment.payment_date)}</td>
                        <td>${payment.currency_details?.title || "USD"}</td>
                        <td>${payment.payment_method || "Cash"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>💰 TOTAL PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        payments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              returns && returns.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                ↩️ Return Transactions (${returns.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Return ID</th>
                      <th style="width: 15%">Purchase ID</th>
                      <th style="width: 20%">Return Date</th>
                      <th style="width: 25%">Reason</th>
                      <th style="width: 15%">Refund Amount</th>
                      <th style="width: 10%">Items Count</th>
                      <th style="width: 5%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${returns
                      .map((returnItem) => {
                        const refundAmount = parseAmount(
                          returnItem.total_refund_amount ||
                            returnItem.return_items?.[0]?.refund_amount ||
                            0,
                        );
                        return `
                        <tr>
                          <td class="id-cell">#${returnItem.id}</td>
                          <td>Purchase #${
                            returnItem.purchase_details?.id ||
                            returnItem.purchase ||
                            "N/A"
                          }</td>
                          <td>${formatDate(returnItem.return_date)}</td>
                          <td>${returnItem.reason || "No reason provided"}</td>
                          <td class="amount-cell">${formatCurrency(
                            refundAmount,
                          )}</td>
                          <td style="text-align: center">${
                            returnItem.return_items?.length || 0
                          }</td>
                          <td><span class="status-badge status-paid">✓</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>🔄 TOTAL RETURNS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_returns?.amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${returns.reduce(
                        (sum, r) => sum + (r.return_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            <div class="footer">
              <div class="footer-stats">
                <div class="footer-stat">
                  <span class="number">${purchases?.length || 0}</span>
                  <span class="label">Purchases</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${payments?.length || 0}</span>
                  <span class="label">Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${returns?.length || 0}</span>
                  <span class="label">Returns</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.outstanding_amount || 0,
                  )}</span>
                  <span class="label">Outstanding</span>
                </div>
              </div>
              <p><strong>📊 Comprehensive Purchase Report</strong> • Generated by Inventory Management System</p>
              <p>Report generated on ${getCurrentDate()}</p>
              <p style="margin-top:8px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</p>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Auto-trigger print dialog after content loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    };
  } else {
    alert("Please allow popups to export the report");
  }

  return printWindow;
};

/**
 * Customer report: company info at top, then full table of customers with
 * previous balance, total sales, and current balance. Data from backend customer-report API.
 */
export const exportCustomerReportToPDF = (data) => {
  const customers = Array.isArray(data?.customers) ? data.customers : [];

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  };

  const parseNumericAmount = (value) => {
    if (value == null) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object") {
      const values = Object.values(value).map((item) => Number(item) || 0);
      return values.reduce((total, item) => total + item, 0);
    }
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const amountMapFromValue = (value, fallbackCurrency = "AFN") => {
    if (value == null) return {};
    if (typeof value === "object" && !Array.isArray(value)) {
      const map = {};
      Object.entries(value).forEach(([currency, amount]) => {
        const num = Number(amount) || 0;
        if (!currency) return;
        map[currency] = (map[currency] || 0) + num;
      });
      return map;
    }
    const num = parseNumericAmount(value);
    if (!num) return {};
    return { [fallbackCurrency]: num };
  };

  const getCurrencyTitle = (...sources) => {
    for (const source of sources) {
      if (!source) continue;
      if (typeof source === "string" && source.trim()) return source.trim();
      if (typeof source === "object") {
        const title =
          source.title ||
          source.name ||
          source.currency_details?.title ||
          source.currency?.title;
        if (title) return String(title).trim();
      }
    }
    return "AFN";
  };

  const addToCurrencyMap = (map, currency, amount) => {
    const key = currency || "AFN";
    const num = Number(amount) || 0;
    if (!num) return;
    map[key] = (map[key] || 0) + num;
  };

  const sumCurrencyMap = (map) =>
    Object.values(map || {}).reduce((total, amount) => total + (Number(amount) || 0), 0);

  const escapeHtml = (value) =>
    String(value ?? "—")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatReportDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const buildCustomerStatements = () =>
    customers.map((item) => {
      const c = item.customer || item;
      const openingCurrency = getCurrencyTitle(
        c.opening_balance_currency_details,
        c.opening_balance_currency,
        "AFN",
      );

      const openingByCurrency = {};
      const openingAmount = Number(c?.opening_balance) || 0;
      if (openingAmount) addToCurrencyMap(openingByCurrency, openingCurrency, openingAmount);

      const salesByCurrency = {};
      const salesPaidByCurrency = {};
      const paidByCurrency = {};
      const returnsByCurrency = {};
      const cashGivenByCurrency = {};
      const youOweByCurrency = {};

      const products = [];

      (item.sales || []).forEach((sale) => {
        const saleCurrency = getCurrencyTitle(
          sale.currency_details,
          sale.currency,
          openingCurrency,
        );
        const saleTotalMap = amountMapFromValue(
          sale.effective_total_amount || sale.total_amount,
          saleCurrency,
        );
        const salePaidMap = amountMapFromValue(sale.total_paid, saleCurrency);

        Object.entries(saleTotalMap).forEach(([currency, amount]) => {
          addToCurrencyMap(salesByCurrency, currency, amount);
        });
        Object.entries(salePaidMap).forEach(([currency, amount]) => {
          addToCurrencyMap(salesPaidByCurrency, currency, amount);
        });

        const saleItems = sale.sale_items || [];
        saleItems.forEach((saleItem) => {
          const qty = Number(saleItem?.quantity) || 0;
          const price = parseNumericAmount(saleItem?.sale_price);
          const total = parseNumericAmount(saleItem?.line_total) || qty * price;
          const itemCurrency = getCurrencyTitle(
            saleItem.currency_details,
            sale.currency_details,
            sale.currency,
            saleCurrency,
          );

          products.push({
            date: sale.sale_date,
            name: saleItem?.product_details?.name || "—",
            qty,
            unit: saleItem?.unit_details?.title || "pcs",
            price,
            total,
            currency: itemCurrency,
          });
        });
      });

      (item.returns || []).forEach((returnItem) => {
        const returnCurrency = getCurrencyTitle(
          returnItem.currency_details,
          returnItem.sale_details?.currency_details,
          openingCurrency,
        );
        let returnTotal = parseNumericAmount(returnItem?.total_refound_amount);
        if (!returnTotal) {
          returnTotal = (returnItem?.return_items || []).reduce(
            (sum, childItem) =>
              sum +
              parseNumericAmount(
                childItem?.refund_amount ??
                  childItem?.calculated_refund_amount?.parsedValue ??
                  childItem?.calculated_refund_amount,
              ),
            0,
          );
        }
        addToCurrencyMap(returnsByCurrency, returnCurrency, returnTotal);
      });

      const payments = [];
      const paidFromPaymentsByCurrency = {};
      (item.balance_payments || []).forEach((payment) => {
        const currency = getCurrencyTitle(
          payment.currency_details,
          payment.currency,
          openingCurrency,
        );
        const cashAmount = parseNumericAmount(payment.amount);
        const reduceAmount = cashAmount + parseNumericAmount(payment.discount);
        payments.push({
          date: payment.payment_date || payment.created_at,
          amount: cashAmount,
          notes: payment.notes || "",
          currency,
        });
        addToCurrencyMap(paidFromPaymentsByCurrency, currency, reduceAmount);
      });
      if (!payments.length) {
        (item.payments || []).forEach((payment) => {
          const currency = getCurrencyTitle(
            payment.currency_details,
            payment.currency,
            openingCurrency,
          );
          const amount = parseNumericAmount(payment.amount);
          payments.push({
            date: payment.payment_date || payment.created_at,
            amount,
            notes: payment.notes || `Sale payment #${payment.id || ""}`.trim(),
            currency,
          });
          addToCurrencyMap(paidFromPaymentsByCurrency, currency, amount);
        });
      }
      payments.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));

      // Paid column must match the payments list whenever payments exist.
      // Fallback to sale.total_paid only when there is no payment history.
      if (sumCurrencyMap(paidFromPaymentsByCurrency)) {
        Object.assign(paidByCurrency, paidFromPaymentsByCurrency);
      } else {
        Object.assign(paidByCurrency, salesPaidByCurrency);
      }

      const cashGivenList = (item.debtors || []).map((debtor) => {
        const currency = getCurrencyTitle(
          debtor.currency_details,
          debtor.currency,
          openingCurrency,
        );
        const amount = parseNumericAmount(debtor.amount);
        const outstanding =
          debtor?.outstanding_amount != null
            ? parseNumericAmount(debtor.outstanding_amount)
            : Math.max(0, amount - parseNumericAmount(debtor?.settled_amount));
        addToCurrencyMap(cashGivenByCurrency, currency, outstanding);
        return {
          date: debtor.loan_date || debtor.created_at,
          amount,
          outstanding,
          notes: debtor.notes || "",
          currency,
        };
      });

      // You Owe: start from API (handles opening/debtor correctly), then credit any
      // payments that were listed but never applied onto unpaid sales (legacy bug).
      const owedFromApi = amountMapFromValue(c.total_owed_by_currency);
      const allCurrenciesForOwe = new Set([
        ...Object.keys(owedFromApi),
        ...Object.keys(openingByCurrency),
        ...Object.keys(salesByCurrency),
        ...Object.keys(cashGivenByCurrency),
        ...Object.keys(returnsByCurrency),
        ...Object.keys(paidByCurrency),
        ...Object.keys(salesPaidByCurrency),
      ]);
      allCurrenciesForOwe.forEach((currency) => {
        const salesPaid = salesPaidByCurrency[currency] || 0;
        const paymentsPaid = paidFromPaymentsByCurrency[currency] || 0;
        const unpaidSales = Math.max(
          0,
          (salesByCurrency[currency] || 0) -
            salesPaid -
            (returnsByCurrency[currency] || 0),
        );
        const unappliedToSales = Math.min(
          Math.max(0, paymentsPaid - salesPaid),
          unpaidSales,
        );

        let owed;
        if (Object.keys(owedFromApi).length) {
          owed = Math.max(0, (owedFromApi[currency] || 0) - unappliedToSales);
        } else {
          owed = Math.max(
            0,
            (openingByCurrency[currency] || 0) +
              (salesByCurrency[currency] || 0) +
              (cashGivenByCurrency[currency] || 0) -
              (returnsByCurrency[currency] || 0) -
              (paidByCurrency[currency] || 0),
          );
        }
        if (owed) youOweByCurrency[currency] = owed;
      });

      const currenciesUsed = [
        ...new Set([
          ...Object.keys(youOweByCurrency),
          ...Object.keys(salesByCurrency),
          ...Object.keys(paidByCurrency),
          ...Object.keys(openingByCurrency),
          ...Object.keys(cashGivenByCurrency),
          ...Object.keys(returnsByCurrency),
        ]),
      ].filter(Boolean);

      if (!currenciesUsed.length) currenciesUsed.push(openingCurrency);

      const currencySections = currenciesUsed.map((currency) => ({
        currency,
        opening: openingByCurrency[currency] || 0,
        sales: salesByCurrency[currency] || 0,
        paid: paidByCurrency[currency] || 0,
        cashGiven: cashGivenByCurrency[currency] || 0,
        returns: returnsByCurrency[currency] || 0,
        youOwe: youOweByCurrency[currency] || 0,
      }));

      return {
        name: c.name || "Customer",
        shop: c.shop_details?.name || c.shop_name || "—",
        currenciesLabel: currenciesUsed.join(" · ") || openingCurrency,
        youOweByCurrency,
        currencySections,
        products,
        payments,
        cashGivenList,
      };
    });

  const customerStatements = buildCustomerStatements();

  const companyName = "Afghan Cosmos ";
  const companyContact = "📞 0788527777 - 0777573777";
  const companyAddress = "🏢 , Kabul, Afghanistan";
  const companyLogo = `${window.location.origin}/logo.jpeg`;

  const customerStatementsHtml = customerStatements
    .map((stmt) => {
      const oweChips = stmt.currencySections
        .map(
          (s) => `
          <div class="owe-chip">
            <div class="owe-chip-cur">${escapeHtml(s.currency)}</div>
            <div class="owe-chip-amt">${formatCurrency(s.youOwe)}</div>
          </div>
        `,
        )
        .join("");

      const summaryRows = stmt.currencySections
        .map(
          (s) => `
          <tr>
            <td><span class="badge">${escapeHtml(s.currency)}</span></td>
            <td class="num">${s.opening ? formatCurrency(s.opening) : "—"}</td>
            <td class="num plus">${s.sales ? formatCurrency(s.sales) : "—"}</td>
            <td class="num plus">${s.cashGiven ? formatCurrency(s.cashGiven) : "—"}</td>
            <td class="num minus">${s.returns ? formatCurrency(s.returns) : "—"}</td>
            <td class="num minus">${s.paid ? formatCurrency(s.paid) : "—"}</td>
            <td class="num owe">${formatCurrency(s.youOwe)}</td>
          </tr>
        `,
        )
        .join("");

      const productRows = stmt.products.length
        ? stmt.products
            .map(
              (row, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${formatReportDate(row.date)}</td>
              <td>${escapeHtml(row.name)}</td>
              <td class="num">${row.qty} ${escapeHtml(row.unit)}</td>
              <td class="num">${formatCurrency(row.price)}</td>
              <td class="num">${formatCurrency(row.total)}</td>
              <td><span class="badge">${escapeHtml(row.currency)}</span></td>
            </tr>
          `,
            )
            .join("")
        : `<tr><td colspan="7" class="empty">No items</td></tr>`;

      const paymentRows = stmt.payments.length
        ? stmt.payments
            .map(
              (row, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${formatReportDate(row.date)}</td>
              <td class="num minus">${formatCurrency(row.amount)}</td>
              <td><span class="badge">${escapeHtml(row.currency)}</span></td>
              <td>${escapeHtml(row.notes || "—")}</td>
            </tr>
          `,
            )
            .join("")
        : `<tr><td colspan="5" class="empty">No payments</td></tr>`;

      const cashRows = stmt.cashGivenList.length
        ? stmt.cashGivenList
            .map(
              (row, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${formatReportDate(row.date)}</td>
              <td class="num plus">${formatCurrency(row.amount)}</td>
              <td class="num owe">${formatCurrency(row.outstanding)}</td>
              <td><span class="badge">${escapeHtml(row.currency)}</span></td>
              <td>${escapeHtml(row.notes || "—")}</td>
            </tr>
          `,
            )
            .join("")
        : "";

      const finalOwe = stmt.currencySections
        .map(
          (s) =>
            `<span class="final-amt">${formatCurrency(s.youOwe)} <small>${escapeHtml(s.currency)}</small></span>`,
        )
        .join("");

      return `
        <div class="customer">
          <div class="customer-head">
            <div>
              <h2>${escapeHtml(stmt.name)}</h2>
              <div class="meta">${escapeHtml(stmt.shop)}</div>
            </div>
          </div>

          <div class="owe-row">${oweChips}</div>

          <div class="card">
            <div class="card-title">Summary by Currency</div>
            <table>
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Opening</th>
                  <th>Bought (+)</th>
                  <th>Cash Given (+)</th>
                  <th>Returns (−)</th>
                  <th>Paid (−)</th>
                  <th>You Owe</th>
                </tr>
              </thead>
              <tbody>${summaryRows}</tbody>
            </table>
          </div>

          <div class="card">
            <div class="card-title">Items Purchased</div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>${productRows}</tbody>
            </table>
          </div>

          <div class="card">
            <div class="card-title">Payments You Made</div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>${paymentRows}</tbody>
            </table>
          </div>

          ${
            cashRows
              ? `
          <div class="card">
            <div class="card-title">Cash Given To ${escapeHtml(stmt.name)} - Customer</div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Still Owe</th>
                  <th>Currency</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>${cashRows}</tbody>
            </table>
          </div>
          `
              : ""
          }

          <div class="final">
            <span>Total You Owe</span>
            <div class="final-list">${finalOwe}</div>
          </div>
        </div>
      `;
    })
    .join("");

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Customer Account Statement</title>
        <meta charset="utf-8">
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          @page { size: A4; margin: 10mm; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 12px;
            color: #1e293b;
            background: #e8eef5;
            padding: 14px;
          }
          .wrap {
            max-width: 840px;
            margin: 0 auto;
            background: #fff;
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 6px 24px rgba(15, 23, 42, 0.08);
          }
          .header {
            text-align: center;
            padding: 18px 16px 16px;
            background: #12344d;
            color: #fff;
          }
          .header img {
            width: 52px;
            height: 52px;
            object-fit: contain;
            margin-bottom: 8px;
            background: #fff;
            border-radius: 10px;
            padding: 4px;
          }
          .header h1 { font-size: 21px; font-weight: 700; margin-bottom: 3px; }
          .header .sub { font-size: 11px; opacity: 0.85; }
          .header .title {
            margin-top: 10px;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }
          .body { padding: 16px 18px 14px; }
          .note {
            background: #f0f7fb;
            border: 1px solid #d7e6f0;
            border-radius: 10px;
            padding: 9px 12px;
            margin-bottom: 14px;
            font-size: 11px;
            color: #334e68;
          }
          .customer { margin-bottom: 6px; }
          .customer-head { margin-bottom: 12px; }
          .customer-head h2 { font-size: 19px; color: #102a43; }
          .meta { font-size: 12px; color: #627d98; margin-top: 2px; }
          .owe-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 14px;
          }
          .owe-chip {
            min-width: 120px;
            background: #f0f7fb;
            border: 1px solid #d7e6f0;
            border-radius: 10px;
            padding: 10px 12px;
            text-align: center;
          }
          .owe-chip-cur {
            font-size: 10px;
            font-weight: 700;
            color: #627d98;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }
          .owe-chip-amt {
            font-size: 16px;
            font-weight: 700;
            color: #12344d;
            margin-top: 2px;
          }
          .card {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            overflow: hidden;
            margin-bottom: 12px;
          }
          .card-title {
            background: #f8fafc;
            padding: 8px 12px;
            font-size: 11px;
            font-weight: 700;
            color: #334e68;
            text-transform: uppercase;
            letter-spacing: 0.35px;
            border-bottom: 1px solid #e2e8f0;
          }
          table { width: 100%; border-collapse: collapse; }
          th, td {
            padding: 7px 9px;
            text-align: left;
            vertical-align: top;
            border-bottom: 1px solid #eef2f6;
          }
          th {
            background: #f8fafc;
            font-size: 10px;
            font-weight: 700;
            color: #627d98;
            text-transform: uppercase;
          }
          td { font-size: 11px; color: #243b53; }
          tr:last-child td { border-bottom: none; }
          .num {
            text-align: right;
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
          }
          .plus { color: #0f7b3a; font-weight: 700; }
          .minus { color: #c81e1e; font-weight: 700; }
          .owe { color: #12344d; font-weight: 700; }
          .empty { text-align: center; color: #829ab1; padding: 12px !important; }
          .badge {
            display: inline-block;
            background: #e6f0f6;
            color: #12344d;
            border-radius: 999px;
            padding: 2px 8px;
            font-size: 10px;
            font-weight: 700;
          }
          .final {
            margin-top: 4px;
            background: #12344d;
            color: #fff;
            border-radius: 10px;
            padding: 12px 14px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            font-size: 13px;
            font-weight: 600;
          }
          .final-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: flex-end;
          }
          .final-amt {
            background: rgba(255,255,255,0.12);
            border-radius: 8px;
            padding: 6px 10px;
            font-size: 14px;
            font-weight: 700;
          }
          .final-amt small {
            font-size: 10px;
            opacity: 0.8;
            font-weight: 600;
          }
          .footer {
            margin-top: 14px;
            padding-top: 10px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            font-size: 10px;
            color: #829ab1;
            line-height: 1.5;
          }
          @media print {
            body { background: #fff; padding: 0; }
            .wrap { box-shadow: none; border-radius: 0; max-width: none; }
            .header, .final, .badge, .owe-chip, .card-title, th {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="wrap">
          <div class="header">
            <img src="${companyLogo}" alt="${escapeHtml(companyName)}" onerror="this.style.display='none'" />
            <h1>${escapeHtml(companyName)}</h1>
            <div class="sub">${companyContact}</div>
            <div class="sub">${companyAddress}</div>
            <div class="title">Customer Account Statement</div>
            <div class="sub">${getCurrentDate()}</div>
          </div>
          <div class="body">
            <div class="note">
              <strong>Guide:</strong>
              Bought &amp; Cash Given = add (+).
              Paid &amp; Returns = reduce (−).
              Each currency total is separate (AFN, USD, …).
            </div>
            ${customerStatementsHtml}
            <div class="footer">
              ${escapeHtml(companyName)} · Customer Account Statement · Generated ${getCurrentDate()}<br/>
              Powered by Afghan Cosmos IT Solutions · www.afghancosmos.com
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 800);
    };
  } else {
    alert("Please allow popups to download the report");
  }
  return printWindow;
};
